import { getComplianceSummary } from '@/api/dashboardApi'
import { complianceSummaryUIMetaData } from '@/constants/dashboardConstant'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// ==================== Constants ====================
const defaultComplianceSummaryData = { total: '', categories: [] }

// ==================== Helpers ====================
// Normalizes the compliance-summary payload for the single-country card. The
// summary lives in the first entry of the response's `countries` array; this
// returns a guaranteed { total, categories } shape (categories always an
// array) so the card never crashes on a missing or malformed entry.
const mapComplianceSummaryToData = (data = {}) => {
  const country = data?.countries?.[0] ?? {}
  return {
    total: country?.total ?? '',
    categories: Array.isArray(country?.categories) ? country.categories : [],
  }
}

const ComplianceOverview = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [data, setData] = useState(defaultComplianceSummaryData)
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const complianceSummaryResponse =
          await getComplianceSummary(appliedFilters)
        processData(complianceSummaryResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Component Helpers ====================
  const processData = complianceSummaryResponse => {
    const complianceSummaryData = complianceSummaryResponse?.data?.data
    if (complianceSummaryResponse?.success && complianceSummaryData) {
      setData(mapComplianceSummaryToData(complianceSummaryData))
    } else {
      setData(defaultComplianceSummaryData)
    }
  }

  // ==================== Return ====================
  return (
    <Box
      sx={{
        border: '1px solid var(--border-subtel)',
        backgroundColor: '#fff',
        flex: '1 1 0',
        minWidth: '300px',
        height: '323px',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="section"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            lineHeight: 1.4,
          }}
        >
          {complianceSummaryUIMetaData.heading}
        </Typography>

        <Tooltip title={complianceSummaryUIMetaData.subHeading}>
          <InfoOutlinedIcon sx={{ width: '17.5px', height: '17.47px' }} />
        </Tooltip>
      </Box>

      {!dataLoaded ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'var(--forbrand-primary)',
              fontSize: 'var(--font-size-lg)',
            }}
          >
            Loading...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.7,
              px: 1.5,
              border: '1px solid var(--border-subtel)',
              borderRadius: '4px',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                color: 'var(--forbrand-primary)',
              }}
            >
              Total
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 500,
                color: 'var(--forbrand-primary)',
              }}
            >
              {data.total}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: 1,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {data.categories.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.7,
                  px: 1.5,
                  border: '1px solid var(--border-subtel)',
                  borderRadius: '4px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 400,
                    color: 'var(--forbrand-primary)',
                  }}
                >
                  {item.category}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 500,
                    color: 'var(--forbrand-primary)',
                  }}
                >
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ComplianceOverview
